import { KeyBinding } from '../../../../shared/CharacterTypes';

/**
 * Convert a browser `keydown` event into the binding key-name vocabulary the
 * engine understands (see InputSystem.mapPhaserKeyToDOMCode — the reverse of
 * this map). Only key names that round-trip through that mapper are valid;
 * anything else would persist a *dead* binding (one InputSystem silently
 * ignores), so we return null and the caller refuses to rebind.
 *
 * Vocabulary (must match DEFAULT_KEY_BINDINGS + mapPhaserKeyToDOMCode):
 *   letters A-Z · digits 0-9 · SPACE ENTER TAB ESCAPE BACKTICK · arrows · left Alt/Shift/Ctrl.
 * Right-side modifiers (ShiftRight/ControlRight/AltRight) return null — only left-side modifiers are supported.
 */
export function domEventToBindingKey(e: Pick<KeyboardEvent, 'code' | 'key'>): string | null {
  const code = e.code;

  // Letters: "KeyA" -> "A"
  if (/^Key[A-Z]$/.test(code)) return code.slice(3);
  // Top-row digits: "Digit1" -> "1"
  if (/^Digit[0-9]$/.test(code)) return code.slice(5);

  const map: Record<string, string> = {
    Space: 'SPACE',
    Enter: 'ENTER',
    Tab: 'TAB',
    Escape: 'ESCAPE',
    NumLock: 'NUMLOCK',
    Backquote: 'BACKTICK',
    AltLeft: 'ALT',
    ShiftLeft: 'SHIFT',
    ControlLeft: 'CTRL',
    ArrowUp: 'UP',
    ArrowDown: 'DOWN',
    ArrowLeft: 'LEFT',
    ArrowRight: 'RIGHT',
    Numpad0: 'NUMPAD_ZERO',
    Numpad1: 'NUMPAD_ONE',
    Numpad2: 'NUMPAD_TWO',
    Numpad3: 'NUMPAD_THREE',
    Numpad4: 'NUMPAD_FOUR',
    Numpad5: 'NUMPAD_FIVE',
    Numpad6: 'NUMPAD_SIX',
    Numpad7: 'NUMPAD_SEVEN',
    Numpad8: 'NUMPAD_EIGHT',
    Numpad9: 'NUMPAD_NINE',
  };

  return map[code] ?? null;
}

/** Stable identity for a binding row (action + key + modifier can repeat by action). */
export const bindingId = (b: KeyBinding): string => `${b.action}::${b.key}::${b.modifier ?? ''}`;

const MODIFIER_CODES = new Set(['ShiftLeft', 'ShiftRight', 'ControlLeft', 'ControlRight', 'AltLeft', 'AltRight']);

/** Whether a `keydown` is a modifier on its own (capture waits to see if a key follows). */
export const isModifierCode = (code: string): boolean => MODIFIER_CODES.has(code);

/** The modifier held with a key press, for a chord such as Shift+1 (one modifier; Shift wins). */
export function eventModifier(e: Pick<KeyboardEvent, 'shiftKey' | 'ctrlKey' | 'altKey'>): KeyBinding['modifier'] {
  return e.shiftKey ? 'shift' : e.ctrlKey ? 'ctrl' : e.altKey ? 'alt' : undefined;
}

const sameKey = (a: Pick<KeyBinding, 'key' | 'modifier'>, key: string, modifier?: string) =>
  a.key.toUpperCase() === key.toUpperCase() && (a.modifier ?? '') === (modifier ?? '');

/**
 * The binding on another action that already holds `key` + `modifier` (one key
 * on two actions is refused, modular bars plan phase 3), or null. Two actions the
 * defaults put on the same key may keep sharing it (Interact and Loot on E,
 * Basic Attack and the fishing keys on F). Shift+1 and 1 are different keys.
 */
export function findKeyHolder(
  action: string,
  key: string,
  modifier: string | undefined,
  bindings: readonly KeyBinding[],
  defaults: readonly KeyBinding[],
): KeyBinding | null {
  const sharedByDefault = (other: string) =>
    defaults.some((d) => d.action === action && sameKey(d, key, modifier))
    && defaults.some((d) => d.action === other && sameKey(d, key, modifier));
  return bindings.find((b) => b.action !== action && sameKey(b, key, modifier) && !sharedByDefault(b.action)) ?? null;
}
